/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { defineMessages } from 'react-intl';

import { fetchGet, fetchPut, fetchDelete, fetchPost } from '../../utils/FetchAPIs';

import { I18NError } from 'Utils';

const serviceURL = '/api/service';

const messages = defineMessages({
  tenantDataError: 'An error occurred while retrieving the tenant data.',
  tenantUsageDataError: 'An error occurred while retrieving the tenant service usage data.',
  sampleAppError: 'An error occurred while retrieving the tenant sample application data.',
  getTenantMembersError: 'An error occurred while retrieving the tenant members.',
  updateTenantMembersError: 'An error occurred while updating the tenant members.',
  sampleAppLoggerError: 'An error occurred while fetching the Sample Application logs.',
  serviceError: 'An error occurred while fetching the services. {message}',
  deleteError: 'An error occurred while deleting the service. {message}',
  addError: 'An error occurred while adding the service. {message}',
  updateError: 'An error occurred while updating the service. {message}',
  updateESMigrationError: 'An error occurred while updating the Event Streams migration.',
  getESMigrationError: 'An error occurred while fetching the Event Streams migration data.',
  completeESMigrationError:
    'An error occurred while updating the Event Streams migration complete.',
});

// Manage the Other Service Configurations
export const getServices = () =>
  fetchGet(serviceURL).catch(error => {
    throw new I18NError(messages.serviceError, error);
  });

export const deleteService = (id, userEmailId) =>
  fetchDelete(serviceURL, [id], { userEmailId }).catch(error => {
    throw new I18NError(messages.deleteError, error);
  });

export const addService = (service, userEmailId) =>
  fetchPost(serviceURL, [service], { userEmailId }).catch(error => {
    throw new I18NError(messages.addError, error);
  });
export const updateService = (service, userEmailId) =>
  fetchPut(serviceURL, [service], { userEmailId }).catch(error => {
    throw new I18NError(messages.updateError, error);
  });

const getTenantData = () => fetchGet(`/api/tenants/services`);

export const getTenantDashboardData = () => {
  const promises = [getTenantData()];
  return Promise.all(promises).then(
    items => ({
      tenant: items[0],
    }),
    error => {
      throw new I18NError(messages.tenantDataError, error);
    }
  );
};

export const getTenantUsageData = () =>
  fetchGet(`/api/usage/tenant`).catch(error => {
    throw new I18NError(messages.tenantUsageDataError, error);
  });

export const getIoTPNumberOfDeviceInUse = () =>
  fetchGet(`/api/tenants/numberOfDevicesInUse`).catch(() => {
    throw new I18NError('Error occured when retrieving IoT Registration Services usage data. ');
  });

export const getCOSUsageData = () =>
  fetchGet(`/api/tenants/cosusage`).catch(() => {
    throw new I18NError('Error occured when retrieving Cloud Object Storage usage data.');
  });

export const getSampleAppData = () =>
  fetchGet(`/api/starterApp`).catch(error => {
    throw new I18NError(messages.sampleAppError, error);
  });

export const getTenantMembers = () =>
  fetchGet(`/api/tenants/members`).catch(error => {
    throw new I18NError(messages.getTenantMembersError, error);
  });

export const updateTenantMembers = members =>
  fetchPut(`/api/tenants/members`, members).catch(error => {
    throw new I18NError(messages.updateTenantMembersError, error);
  });

export const getSampleAppLogs = appGuid =>
  fetchGet(`/api/starterApp/${appGuid}/logs`).catch(error => {
    throw new I18NError(messages.sampleAppLoggerError, error);
  });

export const updateEventStreamMigration = payload =>
  fetchPost(`/api/migration/iotp/connector/enable`, payload).catch(error => {
    throw new I18NError(messages.updateESMigrationError, error);
  });

export const getEventStreamMigration = () =>
  fetchGet(`/api/migration/iotp/connector/status`).catch(error => {
    throw new I18NError(messages.getESMigrationError, error);
  });
export const completeESMigration = () =>
  fetchGet(`/api/migration/iotp/connector/complete`).catch(error => {
    throw new I18NError(messages.completeESMigrationError, error);
  });
